import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { CalendarDay } from '../lib/calendar-day';
import { CalendarState } from '../lib/calendar-state';
import { calculateDaysInMonth } from '../lib/month-calculator';
import { PgCalendarDateGridComponent } from './pg-calendar-date-grid/pg-calendar-date-grid.component';
import { AnimationService } from '../../core/ui/animation.service';
import { styleVariables } from '../../styles/variables';

@Component({
  selector: 'pg-calendar-month, grit-calendar-month',
  templateUrl: './pg-calendar-month.component.html',
  styleUrls: ['./pg-calendar-month.component.scss'],
  exportAs: 'gritCalendarMonth',
})
export class PgCalendarMonthComponent implements OnInit, OnDestroy {
  openMonth: Date;
  subs: Subscription[] = [];
  globalPromise = Promise.resolve();

  currentMonthCmptRef: ComponentRef<PgCalendarDateGridComponent>;
  dynamicSubs = [];

  @ViewChild('monthOutlet', { read: ViewContainerRef, static: true })
  monthOutlet: ViewContainerRef;

  constructor(
    public calendarState: CalendarState,
    private _animation: AnimationService,
    private _cmptFactoryResolver: ComponentFactoryResolver,
    private _renderer: Renderer2
  ) {}

  ngOnInit() {
    this.subs.push(
      this.calendarState.openCalendarMonth.asObservable().subscribe(date => {
        if (this.openMonth) {
          // TODO: some sort of animation thing.
        }
        let direction: any = 'forward';
        if (date < this.openMonth) {
          direction = 'backward';
        }
        this.openMonth = date;
        this._renderMonth(date, { direction: direction });
      })
    );
  }

  ngOnDestroy() {
    this.subs.forEach(s => !s && s.unsubscribe());
  }

  public calculateCalendarGridSize() {
    if (this.currentMonthCmptRef) {
      this.currentMonthCmptRef.instance.calculateGridDimensions();
    }
  }

  private async _renderMonth(
    month: Date,
    options?: { animate?: boolean; direction?: 'forward' | 'backward' }
  ) {
    this.globalPromise = this.globalPromise.then(() =>
      this._renderMonthInternal(month, options)
    );
    return this.globalPromise;
  }

  private async _renderMonthInternal(
    month: Date,
    options?: { animate?: boolean; direction?: 'forward' | 'backward' }
  ): Promise<void> {
    this.dynamicSubs.forEach(s => !s.closed && s.unsubscribe());
    this.dynamicSubs = [];

    const monthArr = calculateDaysInMonth(
      month.getFullYear(),
      month.getMonth(),
      {
        minDate: this.calendarState.minSelectableDate,
        maxDate: this.calendarState.maxSelectableDate,
      }
    );

    const dateGridFactory = this._cmptFactoryResolver.resolveComponentFactory(
      PgCalendarDateGridComponent
    );

    const createdCmptRef = this.monthOutlet.createComponent(dateGridFactory);
    createdCmptRef.instance.monthArray = monthArr;
    createdCmptRef.instance.displayDate = month;

    this.dynamicSubs.push(
      createdCmptRef.instance.dateSelected.subscribe((day: CalendarDay) => {
        this.calendarState.selectedDate.next(day.date);
      })
    );
    if (this.currentMonthCmptRef) {
      const exitingEl = this.currentMonthCmptRef.instance.elementRef
        .nativeElement;
      const enteringEl = createdCmptRef.instance.elementRef.nativeElement;

      this._renderer.setStyle(exitingEl, 'position', 'absolute');
      this._renderer.setStyle(exitingEl, 'top', '0');
      this._renderer.setStyle(exitingEl, 'left', '0');
      this._renderer.setStyle(exitingEl, 'bottom', '0');
      this._renderer.setStyle(exitingEl, 'right', '0');

      this._renderer.setStyle(enteringEl, 'opacity', 0);

      let xVelocity = -32;
      if (options && options.direction && options.direction === 'backward') {
        xVelocity = 32;
      }
      const promises = [
        this._animation.animate({
          targets: exitingEl,

          transform: [
            `translateX(0px) scale(1)`,
            `translateX(${xVelocity}px) scale(.95)`,
          ],
          opacity: [1, 0],
          duration: 150,
          fillMode: 'forwards',
          easing: styleVariables.transitionCurveAcceleration,
        }),

        this._animation.animate({
          targets: enteringEl,

          transform: [`translateX(${-xVelocity}px)`, `translateX(0px)`],
          opacity: [0, 1],
          duration: 180,
          delay: 80,
          fillMode: 'forwards',
          easing: 'linear',
        }),
      ];

      return <any>Promise.all(promises)
        .then(data => {
          this._renderer.setStyle(enteringEl, 'opacity', 1);
          this.currentMonthCmptRef.destroy();
        })
        .then(() => {
          this.currentMonthCmptRef = createdCmptRef;
        });
    } else {
      this.currentMonthCmptRef = createdCmptRef;

      return Promise.resolve();
    }
  }
}
